import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import api from '../utils/request';

const PageWrapper = styled.div`
  padding: 40px 24px;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const IconWrapper = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  
  svg {
    width: 64px;
    height: 64px;
  }
  
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
`;

const BrandIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const BrandText = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
`;

const Message = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const EmailHint = styled.div`
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  
  p {
    margin: 0;
    color: #10b981;
    font-size: 14px;
    font-weight: 500;
  }
`;

const CountdownHint = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 16px 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  }
`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('缺少重置密码令牌，请重新申请找回密码。');
      return;
    }

    // 调用后端API验证token并重置密码
    const resetPassword = async () => {
      try {
        // 指定responseType为text，确保正确接收HTML响应
        const response = await api.get(`/auth/reset-password?token=${encodeURIComponent(token)}`, {
          responseType: 'text'
        });
        
        const htmlText = response.data;
        
        if (htmlText.includes('密码重置成功')) {
          setStatus('success');
          const emailMatch = htmlText.match(/已发送至\s*([^\s<]+)/);
          if (emailMatch) {
            setEmail(emailMatch[1]);
          }
          setMessage('密码重置成功！临时密码已发送至您的邮箱，请查收并登录。');
          
          // 3秒后跳转到登录页
          setCountdown(3);
          const timer = setInterval(() => {
            setCountdown(prev => {
              const next = prev - 1;
              if (next <= 0) {
                clearInterval(timer);
                navigate('/', { replace: true });
                return 0;
              }
              return next;
            });
          }, 1000);
        } else {
          // 解析错误信息
          setStatus('error');
          if (htmlText.includes('链接已过期')) {
            setMessage('重置链接已过期，请重新申请找回密码。');
          } else if (htmlText.includes('链接已失效或被篡改')) {
            setMessage('重置链接无效，请重新申请找回密码。');
          } else if (htmlText.includes('此链接已被使用')) {
            setMessage('此链接已被使用，请重新申请找回密码。');
          } else if (htmlText.includes('账号状态异常')) {
            setMessage('账号状态异常，无法重置密码。如有疑问请联系客服。');
          } else {
            setMessage('密码重置失败，请稍后重试或重新申请找回密码。');
          }
        }
      } catch (error) {
        setStatus('error');
        
        if (error.response && error.response.data) {
          const htmlText = typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data);
          
          if (htmlText.includes('密码重置成功')) {
            setStatus('success');
            const emailMatch = htmlText.match(/已发送至\s*([^\s<]+)/);
            if (emailMatch) {
              setEmail(emailMatch[1]);
            }
            setMessage('密码重置成功！临时密码已发送至您的邮箱，请查收并登录。');
            
            // 3秒后跳转到登录页
            setCountdown(3);
            const timer = setInterval(() => {
              setCountdown(prev => {
                const next = prev - 1;
                if (next <= 0) {
                  clearInterval(timer);
                  navigate('/', { replace: true });
                  return 0;
                }
                return next;
              });
            }, 1000);
            return;
          }
          
          // 解析具体错误
          if (htmlText.includes('链接已过期')) {
            setMessage('重置链接已过期，请重新申请找回密码。');
          } else if (htmlText.includes('链接已失效或被篡改')) {
            setMessage('重置链接无效，请重新申请找回密码。');
          } else if (htmlText.includes('此链接已被使用')) {
            setMessage('此链接已被使用，请重新申请找回密码。');
          } else if (htmlText.includes('账号状态异常')) {
            setMessage('账号状态异常，无法重置密码。');
          } else {
            setMessage('密码重置失败，请稍后重试。');
          }
        } else if (error.request) {
          setMessage('网络错误，请检查您的网络连接。');
        } else {
          setMessage('发生未知错误，请稍后重试。');
        }
      }
    };

    resetPassword();
  }, [searchParams, navigate]);

  const handleBackToHome = () => {
    navigate('/', { replace: true });
  };

  if (status === 'loading') {
    return (
      <PageWrapper>
        <StatusCard>
          <Logo>
            <BrandIcon src="/images/logos/logo.svg" alt="职唛 AI Logo" />
            <BrandText>职唛 AI</BrandText>
          </Logo>
          
          <IconWrapper>
            <Loader className="animate-spin" color="#667eea" />
          </IconWrapper>
          <Title>正在重置密码...</Title>
          <Message>请稍候，正在为您处理密码重置请求。</Message>
        </StatusCard>
      </PageWrapper>
    );
  }

  if (status === 'success') {
    return (
      <PageWrapper>
        <StatusCard>
          <Logo>
            <BrandIcon src="/images/logos/logo.svg" alt="职唛 AI Logo" />
            <BrandText>职唛 AI</BrandText>
          </Logo>
          
          <IconWrapper>
            <CheckCircle color="#10b981" />
          </IconWrapper>
          <Title>密码重置成功</Title>
          <Message>{message}</Message>
          
          {email && (
            <EmailHint>
              <p>临时密码已发送至：{email}</p>
            </EmailHint>
          )}
          
          {countdown > 0 && (
            <CountdownHint>将在 {countdown} 秒后自动跳转到登录页</CountdownHint>
          )}
          
          <ButtonGroup>
            <Button className="primary" onClick={handleBackToHome}>
              立即前往登录
            </Button>
          </ButtonGroup>
        </StatusCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <StatusCard>
        <Logo>
          <BrandIcon src="/images/logos/logo.svg" alt="职唛 AI Logo" />
          <BrandText>职唛 AI</BrandText>
        </Logo>
        
        <IconWrapper>
          <XCircle color="#ef4444" />
        </IconWrapper>
        <Title>密码重置失败</Title>
        <Message>{message}</Message>
        
        <ButtonGroup>
          <Button className="primary" onClick={handleBackToHome}>
            返回首页
          </Button>
        </ButtonGroup>
      </StatusCard>
    </PageWrapper>
  );
};

export default ResetPasswordPage;
